import { css } from '@emotion/react'
import {
    InputHTMLAttributes,
    ReactNode,
    memo,
    useCallback,
    useEffect,
    useState,
} from 'react'
import { getScreenState } from '../../app-state/state.screen'
import { updatePortalState } from '../../state/state.layout'
import { AppRouter } from '../../state/state.router'
import { isAndroid, isIpad } from '../../utilities/browser'
import { useScreenStateSelector } from '../Hooks/useScreenState'
import { palette } from '../styles/colors'
import shape from '../styles/shape'

const inputStyles = css({
    font: 'inherit',
    letterSpacing: 'inherit',
    outline: 'none',
    background: 'none',
    color: 'currentcolor',
    flex: 1,
    minWidth: 0,
    border: 0,
    margin: 0,
    padding: '4px 0 5px',
    height: '1.4375rem',
    display: 'block',
    caretColor: palette.greenMFC,
    WebkitTapHighlightColor: 'transparent',
    '&::placeholder': {
        color: palette.dimContrastIcons,
    },
})

const outlinedBorderStyles = css({
    position: 'absolute',
    inset: '-1px -3px -1px',
    pointerEvents: 'none',
    borderRadius: 'inherit',
    border: '1px solid ' + palette.dimContrastIcons,
    '&:hover': {
        border: '2px solid ' + palette.active,
    },
})
const focusedOutlinedBorderStyles = css({
    border: '2px solid ' + palette.greenMFC,
    '&:hover': {
        border: '2px solid ' + palette.greenMFC,
    },
})

const outlinedStyles = css({
    padding: '15px 0',
})
const outlinedContainerStyles = css({
    border: '1px solid ' + palette.dimContrastIcons,
    borderRadius: shape.borderRadius,
    padding: '0 14px',
    '&:hover': {
        border: '1px solid ' + palette.active,
    },
})

const fullWidthContainerStyles = css({
    width: '100%',
})

const inputContainerStyles = css({
    fontSize: 13,
    fontWeight: 500,
    display: 'inline-flex',
    flex: 1,
    alignItems: 'center',
    position: 'relative',
    lineHeight: '1.4375rem',
})

const shouldPinVideo = () => {
    const isMobile = window.isIOS || isIpad || isAndroid
    const { windowHeight } = getScreenState()
    const current = window?.visualViewport?.height || window?.innerHeight
    const isKeyboardOpen =
        windowHeight && current ? windowHeight - current > 1e-7 : false
    const zoom =
        window?.visualViewport?.scale && window?.visualViewport.scale - 1 > 1e-7
    return isKeyboardOpen && !zoom && isMobile
}

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
    ref?: any
    outlined?: boolean
    fullWidth?: boolean
    quietFocus?: boolean
    noTrackFocus?: boolean
    endAdornment?: ReactNode
    startAdornment?: ReactNode
}

const Input = memo((props: InputProps) => {
    const {
        ref,
        className,
        outlined = false,
        fullWidth = false,
        quietFocus = false,
        noTrackFocus = false,
        endAdornment,
        startAdornment,
        onFocus,
        onBlur,
        ...directProps
    } = props
    const [focused, setFocused] = useState(false)
    const portrait = useScreenStateSelector('portrait')

    const onInputFocus = useCallback(
        (event) => {
            if (!noTrackFocus) {
                // if we're mobile and the keyboard is open, then we shouldn't quiet focus,
                // and if we are on desktop, then we should basically always quiet focus
                if (isIpad) {
                    setTimeout(() => {
                        const shouldPinToTop = shouldPinVideo()
                        updatePortalState(
                            {
                                composeFocused: true,
                                quietFocus: quietFocus || !shouldPinToTop,
                            },
                            false,
                        )
                    }, 100)
                } else {
                    const shouldPinToTop = shouldPinVideo()
                    updatePortalState(
                        {
                            composeFocused: true,
                            quietFocus: quietFocus || !shouldPinToTop,
                        },
                        false,
                    )
                }
            }
            setFocused(true)
            onFocus?.(event)
        },
        [onFocus, quietFocus, noTrackFocus],
    )
    const onInputBlur = useCallback(
        (event) => {
            if (!noTrackFocus) {
                updatePortalState(
                    {
                        composeFocused: false,
                        quietFocus: quietFocus,
                    },
                    false,
                )
            }
            setFocused(false)
            onBlur?.(event)
        },
        [onBlur, quietFocus, noTrackFocus],
    )
    useEffect(() => {
        if (window.isIOS || isIpad || isAndroid) {
            // orientation has changed
            ref?.current?.blur()
            updatePortalState({
                composeFocused: false,
                quietFocus: quietFocus,
            })
        }
    }, [portrait, quietFocus])
    useEffect(() => {
        const onResize = () => {
            // this will run when keyboard opens to correct it if the keyboard opens
            const shouldPinToTop = shouldPinVideo()
            if (!noTrackFocus) {
                updatePortalState(
                    {
                        quietFocus: quietFocus || !shouldPinToTop,
                    },
                    false,
                )
            }
            if (!shouldPinToTop) {
                // if the keyboard got closed (keyboard was dismissed) we should blur the input so user
                // can properly interact with the rest of the room (like dragging down video) without
                // keyboard popping open again
                ref?.current?.blur()
            }
            // only scroll if we're on iPad, this particular input is focused, it's not a quietFocus input, and we're in room
            if (
                isIpad &&
                !quietFocus &&
                focused &&
                AppRouter.route.visiblePage === 'room'
            ) {
                // force scroll incase ipad safari didn't push the viewport as it should
                window.scrollBy(0, 2000)
            }
        }
        window.visualViewport?.addEventListener?.('resize', onResize, {
            passive: true,
        })
        return () => {
            window.visualViewport?.removeEventListener?.('resize', onResize)
        }
    }, [quietFocus, focused, noTrackFocus])
    return (
        <div
            css={[
                inputContainerStyles,
                fullWidth && fullWidthContainerStyles,
                outlined && outlinedContainerStyles,
            ]}
            className={className}
        >
            {startAdornment}
            <input
                css={[
                    inputStyles,
                    // focused && focusedStyles,
                    outlined && outlinedStyles,
                ]}
                className={`mfc-input ${focused ? 'mfc-focused' : ''}`}
                ref={ref}
                onFocus={onInputFocus}
                onBlur={onInputBlur}
                autoComplete="off"
                type="search"
                {...directProps}
            />
            {endAdornment}

            {outlined && (
                <fieldset
                    css={[
                        outlinedBorderStyles,
                        focused && focusedOutlinedBorderStyles,
                    ]}
                />
            )}
        </div>
    )
})

export default Input
